import { ArgumentsType } from 'interfaces'
import { FutureLayerType } from 'components/features/future/types'

export const MISSING_TIMEFRAME_DATA_MESSAGE = 'No data available for the selected time period.'

export const NON_FIELD_ERRORS = 'non_field_errors'
export const DEFAULT_NON_FIELD_ERROR = 'non-field-error'
export const CANNOT_BE_BLANK = 'cannot-be-blank'
export const TYPE_REQUIRED = 'required'
export const TYPE_EMAIL_INVALID = 'invalid-email'
export const TYPE_EMAIL_EXISTS = 'existed-email'
export const TYPE_GROUP_NAME_EXISTS = 'existing-group-name'
export const BIRTH_DATE_TOO_YOUNG = 'too-young'
export const BIRTH_DATE_TOO_OLD = 'too-old'
export const DATE_INVALID_SHORT = 'invalid-date-short'
export const DATE_INVALID = 'invalid-date'
export const DATE_IN_PAST = 'date-in-past'
export const AGE_IN_PAST = 'age-in-past'
export const CURRENCY_INVALID_SHORT = 'invalid-currency-short'
export const NONZERO_CURRENCY_INVALID = 'invalid-nonzero-currency'

export const LOCATION_INVALID = 'invalid-location'

export const TYPE_UNMATCHED_PASSWORD = 'unmatched-password'
export const TYPE_UNMATCHED_SSN = 'unmatched-ssn'
export const TYPE_UNMATCHED_ACCOUNT = 'unmatched-account'
export const POLICY_NOT_CONFIRMED = 'policy-not-confirmed'

export const FEED_USERNAME_EXISTS = 'feed-username-exists'
export const FEED_USERNAME_INVALID = 'invalid-feed-username'

// we have also 'user-blocked' error in credit linkage, both that and this differ
// this one we consider as member.is_blocked
// TODO unify them or investigate how to avoid this confusing
export const CREDIT_USER_BLOCKED = 'user_blocked'

export const ROUTING_NUMBER_ERROR = 'routing-number-error'
export const ACCOUNT_NUMBER_ERROR = 'account-number-error'
export const MIN_REDEMPTION_AMOUNT = 'min-redemption-amount'
export const MAX_REDEMPTION_AMOUNT = 'max-redemption-amount'

const ALREADY_MARRIED_API_TEXT = 'GetMarried is not allowed: already married'
const ALREADY_SINGLE_API_TEXT = 'GetDivorced is not allowed: already divorced'
const INVALID_NUMBER_API_TEXT = 'A valid number is required.'
const INVALID_ASSET_API_TEXT = 'does_not_exist'

export const ADVISOR_TIMESLOT_BUSY = 'Timeslot is not free for this advisor'

export const REQUIRED_TEXT = 'This field is required'
export const CARD_NUMBER_ERROR = 'Card number must be 16 digits long'

export const REQUEST_ERROR = 'request-error'

export const LOW_BALANCE = 'user_has_low_balance'
export const AMOUNT_TOO_LOW = 'amount_too_low'

export const CURRENCY_NOT_SUPPORTED = 'not_supported'

export const INCORRECT_VERIFICATION_CODE = 'incorrect' as const
export const MUST_REVALIDATE_PHONE_NUMBER = 'must_revalidate_phone_number' as const

const SSN_REQUIRED = 'social_security_number may not be empty' as const
const PHONE_REQUIRED = 'phone_number may not be empty' as const

const VALIDATION_ERROR_MAP_WITHOUT_ARGS = {
  [DEFAULT_NON_FIELD_ERROR]: () => 'Can\'t perform action',
  [CANNOT_BE_BLANK]: () => 'Please select a value',
  [TYPE_REQUIRED]: () => REQUIRED_TEXT,
  [TYPE_EMAIL_INVALID]: () => 'Please enter a valid email',
  [TYPE_EMAIL_EXISTS]: () => 'This email is associated with an existing account',
  [TYPE_GROUP_NAME_EXISTS]: () => 'You already have a group with this name',
  [TYPE_UNMATCHED_PASSWORD]: () => 'The passwords provided do not match',
  [TYPE_UNMATCHED_SSN]: () => 'The SSNs provided do not match',
  [TYPE_UNMATCHED_ACCOUNT]: () => 'This field should match your Account Number',
  [CREDIT_USER_BLOCKED]: () => 'We are unable to verify your identity using the information you provided.',

  [CURRENCY_INVALID_SHORT]: () => 'Invalid currency',
  [NONZERO_CURRENCY_INVALID]: () => 'Enter $1 or more',
  [DATE_INVALID]: () => 'Please enter a valid date',
  [DATE_INVALID_SHORT]: () => 'Invalid date',
  [DATE_IN_PAST]: () => 'The date must be in the future',
  [AGE_IN_PAST]: () => 'The age must be in the future',
  [BIRTH_DATE_TOO_YOUNG]: () => 'Sorry, you must be at least 18 to register for Status',
  [BIRTH_DATE_TOO_OLD]: () => 'Please enter a valid birth date',

  [LOCATION_INVALID]: () => 'Please select a city from the list',
  [POLICY_NOT_CONFIRMED]: () => 'Please confirm this box checked to proceed',
  [ROUTING_NUMBER_ERROR]: () => 'The routing number must be 9 digits',
  [ACCOUNT_NUMBER_ERROR]: () => `Invalid account number`,
  [MAX_REDEMPTION_AMOUNT]: () => `Redemption can't exceed available balance`,
  [FEED_USERNAME_EXISTS]: () => `This nickname is taken`,
  [FEED_USERNAME_INVALID]: () => `Please use english letters, digits and ._-`,

  [INVALID_NUMBER_API_TEXT]: () => REQUIRED_TEXT,
  [INVALID_ASSET_API_TEXT]: () => REQUIRED_TEXT,

  [ADVISOR_TIMESLOT_BUSY]: () => `Sorry, that appointment time is no longer available. Please choose a different timeslot.`,
  [LOW_BALANCE]: () => `Not enough balance`,
  [AMOUNT_TOO_LOW]: () => `Order value too low`,
  [CURRENCY_NOT_SUPPORTED]: () => `Currency not supported`,

  [INCORRECT_VERIFICATION_CODE]: () => `Incorrect verification code`,
  [MUST_REVALIDATE_PHONE_NUMBER]: () => `Too many attempts`,

  [SSN_REQUIRED]: () => 'Social Security Number is required',
  [PHONE_REQUIRED]: () => 'Phone Number is required',
  [REQUEST_ERROR]: () => 'Request unexpected error',
}

// These errors shouldn't be used by the server, because frontend doesn't receive arguments from backend
const VALIDATION_ERROR_MAP_WITH_ARGS = {
  [MIN_REDEMPTION_AMOUNT]: (amount: number) => `Redemption amount must be $${ amount } or more`,

  [ALREADY_MARRIED_API_TEXT]: (layerType: FutureLayerType) => `You’re already married at this time. Edit this ${ layerType } before enabling it.`,
  [ALREADY_SINGLE_API_TEXT]: (layerType: FutureLayerType) => `You’re already single at this time. Edit this ${ layerType } before enabling it.`,
}

const VALIDATION_ERROR_MAP = {
  ...VALIDATION_ERROR_MAP_WITHOUT_ARGS,
  ...VALIDATION_ERROR_MAP_WITH_ARGS,
}

type ValidationErrorMap = typeof VALIDATION_ERROR_MAP
type ValidationErrorKeys = keyof ValidationErrorMap
export type ValidationErrorKeysWithArgs = typeof VALIDATION_ERROR_MAP_WITH_ARGS
export type ValidationErrorKeysWithoutArgs = Exclude<ValidationErrorKeys, ValidationErrorKeysWithArgs>

export const MAX_ENROLLMENTS_ERROR_CODE = 'max_enrollments_exceeded'
export const TUCI_INTERNAL_ERROR = 'internal_error'
export const TUCI_VERIFICATION_FAILED = 'verification_failed'

export function resolveErrorById<TErrorId extends ValidationErrorKeys> (
  errorId: TErrorId,
  ...args: ArgumentsType<ValidationErrorMap[TErrorId]>
): string {
  const errorGenerator = VALIDATION_ERROR_MAP[errorId]
  return errorGenerator ? errorGenerator.apply(this, args) : errorId
}

export default resolveErrorById
